import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { META_MAX, META_MIN, gerarSlug } from "@/lib/admin/validacao";

type Categoria = {
  id: string | null;
  slug: string;
  label: string;
  intro: string;
  sort_order: string;
  seo_title: string;
  meta_description: string;
};

export const Route = createFileRoute("/admin/categorias")({
  ssr: false,
  head: () => ({ meta: [{ title: "Categorias do blog | Painel" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: Categorias,
});

const campo =
  "w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring";
const rotulo = "block text-xs font-medium text-foreground";

const vazia: Categoria = { id: null, slug: "", label: "", intro: "", sort_order: "", seo_title: "", meta_description: "" };

function Categorias() {
  const [lista, setLista] = useState<Categoria[]>([]);
  const [mensagem, setMensagem] = useState<string | null>(null);

  async function carregar() {
    const { data } = await supabase
      .from("post_categories")
      .select("id, slug, label, intro, sort_order, seo_title, meta_description")
      .order("sort_order");
    setLista(
      (data ?? []).map((c) => ({
        id: c.id,
        slug: c.slug,
        label: c.label,
        intro: c.intro ?? "",
        sort_order: c.sort_order == null ? "" : String(c.sort_order),
        seo_title: c.seo_title ?? "",
        meta_description: c.meta_description ?? "",
      })),
    );
  }

  useEffect(() => {
    void carregar();
  }, []);

  function alterar(i: number, chave: keyof Categoria, valor: string) {
    setLista((l) =>
      l.map((c, j) => {
        if (j !== i) return c;
        const nova = { ...c, [chave]: valor };
        if (chave === "label" && !c.id) nova.slug = gerarSlug(valor);
        return nova;
      }),
    );
  }

  async function salvar(c: Categoria) {
    setMensagem(null);
    if (!c.label.trim() || !c.slug.trim()) return setMensagem("Nome e slug são obrigatórios.");
    const meta = c.meta_description.trim().length;
    if (meta > 0 && (meta < META_MIN || meta > META_MAX))
      return setMensagem(`A meta description precisa ter entre ${META_MIN} e ${META_MAX} caracteres. Agora tem ${meta}.`);
    const registro = {
      slug: gerarSlug(c.slug),
      label: c.label.trim(),
      intro: c.intro.trim() || null,
      sort_order: c.sort_order ? Number(c.sort_order) : null,
      seo_title: c.seo_title.trim() || null,
      meta_description: c.meta_description.trim() || null,
      updated_at: new Date().toISOString(),
    };
    const r = c.id
      ? await supabase.from("post_categories").update(registro).eq("id", c.id)
      : await supabase.from("post_categories").insert(registro);
    if (r.error) return setMensagem(`Não foi possível salvar: ${r.error.message}`);
    setMensagem(`Categoria "${registro.label}" salva.`);
    void carregar();
  }

  async function excluir(c: Categoria) {
    if (!c.id) return setLista((l) => l.filter((x) => x !== c));
    if (!window.confirm(`Excluir a categoria "${c.label}"? A página /blog/${c.slug}/ deixa de existir.`)) return;
    const { count } = await supabase.from("posts").select("id", { count: "exact", head: true }).eq("category_id", c.id);
    if (count) return setMensagem(`Não dá para excluir: ${count} artigo(s) usam esta categoria. Mude a categoria deles antes.`);
    const r = await supabase.from("post_categories").delete().eq("id", c.id);
    if (r.error) return setMensagem(`Não foi possível excluir: ${r.error.message}`);
    void carregar();
  }

  return (
    <section>
      <h1 className="text-lg font-semibold text-foreground">Categorias do blog</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Cada categoria vira uma página própria em /blog/slug/. O slug de categoria já publicada não deve mudar.
      </p>
      {mensagem ? (
        <p className="mt-4 rounded-md border border-border bg-muted px-3 py-2 text-sm text-foreground">{mensagem}</p>
      ) : null}

      <div className="mt-6 space-y-4">
        {lista.map((c, i) => (
          <div key={c.id ?? `nova-${i}`} className="grid gap-3 rounded-md border border-border p-4 md:grid-cols-2">
            <div>
              <label className={rotulo} htmlFor={`label-${i}`}>Nome</label>
              <input id={`label-${i}`} className={campo} value={c.label} onChange={(e) => alterar(i, "label", e.target.value)} />
            </div>
            <div>
              <label className={rotulo} htmlFor={`slug-${i}`}>Slug</label>
              <input id={`slug-${i}`} className={campo} value={c.slug} onChange={(e) => alterar(i, "slug", gerarSlug(e.target.value))} />
            </div>
            <div className="md:col-span-2">
              <label className={rotulo} htmlFor={`intro-${i}`}>Introdução da página da categoria</label>
              <textarea id={`intro-${i}`} rows={2} className={campo} value={c.intro} onChange={(e) => alterar(i, "intro", e.target.value)} />
            </div>
            <div>
              <label className={rotulo} htmlFor={`seo-${i}`}>Title (SEO)</label>
              <input id={`seo-${i}`} className={campo} value={c.seo_title} onChange={(e) => alterar(i, "seo_title", e.target.value)} />
            </div>
            <div>
              <label className={rotulo} htmlFor={`ordem-${i}`}>Ordem</label>
              <input id={`ordem-${i}`} type="number" className={campo} value={c.sort_order} onChange={(e) => alterar(i, "sort_order", e.target.value)} />
            </div>
            <div className="md:col-span-2">
              <label className={rotulo} htmlFor={`meta-${i}`}>Meta description</label>
              <textarea id={`meta-${i}`} rows={2} className={campo} value={c.meta_description} onChange={(e) => alterar(i, "meta_description", e.target.value)} />
              <p className="mt-1 text-xs text-muted-foreground">
                {c.meta_description.trim().length} caracteres. Faixa exigida: {META_MIN} a {META_MAX}.
              </p>
            </div>
            <div className="flex gap-2 md:col-span-2">
              <Button type="button" onClick={() => void salvar(c)}>Salvar</Button>
              <Button type="button" variant="ghost" onClick={() => void excluir(c)}>Excluir</Button>
            </div>
          </div>
        ))}
      </div>

      <Button type="button" variant="outline" className="mt-4" onClick={() => setLista((l) => [...l, { ...vazia }])}>
        Nova categoria
      </Button>
    </section>
  );
}
